import { useEffect, useMemo, useState } from "react";
import HeroBanner from "../components/landing/HeroBanner.jsx";
import WhyHomevilasVideoCarousel from "../components/Home/WhyHomevilasVideoCarousel.jsx";
import JournalGrid from "../legacy/components/landingSnapshot/JournalGrid.jsx";
import PopularDestinations from "../legacy/components/landingSnapshot/PopularDestinations.jsx";
import StayRow from "../legacy/components/landingSnapshot/StayRow.jsx";
import { apiGet } from "../legacy/services/apiClient";
import { publicBrandText } from "../utils/brandText";
import { buildElivaasDetailPath, extractElivaasProviderId } from "../utils/providerRoutes";
import { resolveAssetUrl } from "../legacy/utils/resolveAssetUrl.js";

const safeArray = (value) => (Array.isArray(value) ? value : []);

const resolveImage = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^data:image\//i.test(raw)) return raw;
  if (/^https?:\/\//i.test(raw)) return resolveAssetUrl(raw);
  if (raw.startsWith("/landing/") || raw.startsWith("landing/")) {
    return raw.startsWith("/") ? raw : `/${raw}`;
  }
  return resolveAssetUrl(raw);
};

const resolveFromKeys = (item, keys) => {
  for (const key of keys) {
    const value = item?.[key];
    if (value) return resolveImage(value);
  }
  return "";
};

const normalizeSideBanner = (banner, fallbackId) => {
  if (!banner) return null;
  const meta =
    banner.meta && typeof banner.meta === "object" ? { ...banner.meta } : {};
  const kicker =
    banner.kicker ||
    meta.kicker ||
    meta.eyebrow ||
    banner.eyebrow ||
    "";
  if (kicker && !meta.kicker) meta.kicker = kicker;
  if (kicker && !meta.eyebrow) meta.eyebrow = kicker;
  const image = resolveImage(
    banner.image || banner.imageUrl || banner.image_url || ""
  );
  return {
    id: banner.id || banner._id || fallbackId,
    title: banner.title || "",
    subtitle: banner.subtitle || "",
    image,
    imageUrl: image,
    ctaText: banner.ctaText || banner.cta_text || banner.cta || "",
    ctaLink: banner.ctaLink || banner.cta_link || banner.link || "",
    textAlignment:
      banner.textAlignment || banner.text_alignment || banner.align || "left",
    textSize: banner.textSize || banner.text_size || "h2",
    textColor: banner.textColor || banner.text_color || "",
    fontWeight: banner.fontWeight || banner.font_weight,
    meta,
    kicker,
    theme: banner.theme || meta.theme || "dark",
  };
};

const hasMeaningfulSideBannerContent = (banner) =>
  Boolean(
    String(banner?.kicker || "").trim() ||
      String(banner?.title || "").trim() ||
      String(banner?.subtitle || "").trim() ||
      String(banner?.ctaText || banner?.cta || "").trim() ||
      String(banner?.ctaLink || "").trim() ||
      String(banner?.meta?.kicker || banner?.meta?.eyebrow || "").trim()
  );

const normalizeTopBanner = (banner, fallbackId) => ({
  id: banner?.id || banner?._id || fallbackId,
  title: banner?.title || "",
  subtitle: banner?.subtitle || "",
  imageUrl: resolveImage(banner?.imageUrl || banner?.image_url || banner?.image),
  ctaText: banner?.ctaText || banner?.cta_text || "",
  ctaLink: banner?.ctaLink || banner?.cta_link || banner?.link || "",
  textAlignment: banner?.textAlignment || banner?.text_alignment || banner?.align || "",
  textSize: banner?.textSize || banner?.text_size || "",
  textColor: banner?.textColor || banner?.text_color || "",
  fontWeight: banner?.fontWeight || banner?.font_weight,
  meta: banner?.meta || {},
});

const formatBlogDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", { month: "short", year: "numeric" });
};

const isStaticDemoPropertySlug = (value) => /^hv-demo-|^demo-/i.test(String(value || "").trim());

const buildListingFallbackLink = (item = {}) => {
  const location = item?.city || item?.location || "";
  const params = new URLSearchParams();
  if (location) params.set("location", String(location).split(",")[0].trim());
  return `/listings${params.toString() ? `?${params.toString()}` : ""}`;
};

const resolveStaticStayLink = (item = {}) => {
  const slug = String(item?.slug || "").trim();
  const link = String(item?.link || "").trim();
  if (isStaticDemoPropertySlug(slug) || /\/property\/(?:hv-demo-|demo-)/i.test(link)) {
    return buildListingFallbackLink(item);
  }
  return link || (slug ? `/property/${slug}` : buildListingFallbackLink(item));
};

const buildLockedView = (data) => {
  const hero = data?.hero || {};
  const heroCards = safeArray(hero.sliderImages).map((item, index) => ({
    id: item?.id || `hero-${index + 1}`,
    title: item?.title || "",
    location: item?.location || item?.subtitle || "",
    tag: item?.tag || "",
    meta: item?.meta || "",
    image: resolveFromKeys(item, ["image", "image_url", "imageUrl", "image_path", "imagePath"]),
  }));

  const heroContent = {
    heroEyebrow: hero?.eyebrow || "",
    heroHeadline: hero?.heading || "",
    heroSubhead: hero?.subheading || "",
    heroPrimaryLabel: hero?.cta1?.label || "",
    heroPrimaryLink: hero?.cta1?.link || "",
    heroSecondaryLabel: hero?.cta2?.label || "",
    heroSecondaryLink: hero?.cta2?.link || "",
    heroFootnote: hero?.footnote || "",
    locationPills: safeArray(hero?.locationPills),
  };

  const services = safeArray(data?.services)
    .slice(0, 3)
    .map((item, index) => ({
      id: item?.id || `service-${index + 1}`,
      title: item?.title || "",
      subtitle: item?.subtitle || "",
      cta: item?.cta || item?.ctaText || item?.cta_text || "",
      ctaLink: item?.ctaLink || item?.cta_link || "",
      theme: item?.theme || "",
      image: resolveFromKeys(item, ["image", "image_url", "imageUrl", "image_path", "imagePath"]),
      icon: item?.icon || "",
    }));

  const rails = safeArray(data?.rails).map((rail, index) => {
    const topBanners = safeArray(rail?.topBanners).map((banner, bannerIndex) => ({
      id: banner?.id || `banner-${index + 1}-${bannerIndex + 1}`,
      title: banner?.title || "",
      subtitle: banner?.subtitle || "",
      imageUrl: resolveImage(banner?.imageUrl || banner?.image_url || banner?.image),
      ctaText: banner?.ctaText || banner?.cta_text || "",
      ctaLink: banner?.ctaLink || banner?.cta_link || "",
      textAlignment: banner?.textAlignment || banner?.text_alignment || "",
      textSize: banner?.textSize || banner?.text_size || "",
      textColor: banner?.textColor || banner?.text_color || "",
      fontWeight: banner?.fontWeight || banner?.font_weight,
      meta: banner?.meta || {},
    }));

    const sideBannerRaw = rail?.sideBanner || rail?.side_banner || rail?.banner;
    const sideBanner = normalizeSideBanner(
      sideBannerRaw,
      `side-${index + 1}`
    );

    const cards = safeArray(rail?.items).map((item, cardIndex) => ({
      id: item?.id || `stay-${index + 1}-${cardIndex + 1}`,
      slug: item?.slug || "",
      title: item?.title || "",
      location: item?.location || "",
      price: item?.price,
      rating: item?.rating,
      badge: item?.badge || "",
      image: resolveFromKeys(item, [
        "image",
        "image_url",
        "imageUrl",
        "cover_image",
        "coverImage",
        "cover",
      ]),
      link: resolveStaticStayLink(item),
      guests: item?.guests,
      bedrooms: item?.bedrooms,
      bathrooms: item?.bathrooms,
      inventory: item?.inventory,
      propertyType: item?.property_type || item?.propertyType || item?.type || "",
      amenities: safeArray(item?.amenities),
    }));

    return {
      id: rail?.key === "featured" ? "upcoming" : rail?.key || `rail-${index + 1}`,
      sectionKey: rail?.key || `rail-${index + 1}`,
      eyebrow: rail?.eyebrow || "",
      title: rail?.title || "",
      subtitle: rail?.subtitle || "",
      actionLabel: rail?.actionLabel || "",
      actionLink: rail?.actionLink || "",
      topBanners,
      sideBanner,
      cards,
    };
  });

  const destinations = safeArray(data?.destinations).map((item, index) => ({
    id: item?.id || `destination-${index + 1}`,
    title: item?.title || "",
    subtitle: item?.subtitle || "",
    image: resolveFromKeys(item, ["image", "image_url", "imageUrl", "image_path", "imagePath"]),
    link: item?.link || "",
  }));

  const blogs = safeArray(data?.blog)
    .slice(0, 4)
    .map((item, index) => ({
      id: item?.id || `blog-${index + 1}`,
      title: item?.title || "",
      date: formatBlogDate(item?.publishedAt || item?.published_at || item?.createdAt || item?.created_at),
      excerpt: item?.excerpt || "",
      image: resolveFromKeys(item, ["image", "image_url", "imageUrl", "image_path", "imagePath"]),
      link: item?.link || "",
    }));

  const confidence = data?.confidence || {};
  const confidenceItems = safeArray(confidence?.items).map((item, index) => ({
    id: item?.id || `confidence-${index + 1}`,
    title: item?.title || "",
    desc: item?.desc || "",
  }));

  const logos = safeArray(data?.partners).map((item, index) => ({
    id: item?.id || `partner-${index + 1}`,
    name: item?.name || item?.label || "",
    logoUrl: resolveFromKeys(item, ["logoUrl", "logo", "logo_url", "image", "image_url"]),
  }));

  return {
    heroCards,
    heroContent,
    services,
    rails,
    destinations,
    blogs,
    confidenceHeader: {
      eyebrow: confidence?.eyebrow || "",
      title: confidence?.title || "",
      subtitle: confidence?.subtitle || "",
    },
    confidenceBackgroundImage: resolveImage(
      confidence?.background_image_url || confidence?.backgroundImageUrl || confidence?.backgroundImage || ""
    ),
    confidenceItems,
    logos,
  };
};

const EMPTY_VIEW = buildLockedView({});
const toLocalIsoDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const buildDefaultProviderDateRange = () => {
  const checkInDate = new Date();
  const checkOutDate = new Date(checkInDate);
  checkOutDate.setDate(checkOutDate.getDate() + 1);
  return {
    checkIn: toLocalIsoDate(checkInDate),
    checkOut: toLocalIsoDate(checkOutDate),
  };
};

const runWhenIdle = (callback, timeout = 1800) => {
  if (typeof window === "undefined") {
    callback();
    return () => {};
  }

  if (typeof window.requestIdleCallback === "function") {
    const id = window.requestIdleCallback(callback, { timeout });
    return () => window.cancelIdleCallback?.(id);
  }

  const id = window.setTimeout(callback, timeout);
  return () => window.clearTimeout(id);
};

const toPositiveNumber = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) && amount > 0 ? amount : null;
};

const normalizeLocationLabel = (item) =>
  [item?.city, item?.state, item?.country].map((value) => String(value || "").trim()).filter(Boolean).join(", ");

const mediaUrlValue = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value !== "object") return "";
  return (
    value.image_url ||
    value.imageUrl ||
    value.url ||
    value.src ||
    value.mobileUrl ||
    value.mobile_url ||
    value.thumbnailUrl ||
    value.thumbnail_url ||
    value.defaultMedia?.url ||
    value.defaultMedia?.mobileUrl ||
    ""
  );
};

const mapPublicStayCard = (item, index, badgeOverride = "") => {
  const providerPropertyId = String(
    extractElivaasProviderId(item) || item?.provider_property_id || item?.providerPropertyId || item?.id || `homevilas-${index + 1}`
  ).trim();
  const providerLink = buildElivaasDetailPath({ ...item, provider_property_id: providerPropertyId });
  const title = publicBrandText(item?.title || item?.name || item?.property_name || item?.propertyName || providerPropertyId);
  const location = normalizeLocationLabel(item) || "India";
  const rawImage = mediaUrlValue(
    item?.thumbnail ||
      item?.thumbnail_url ||
      item?.featured_image ||
      item?.coverImage ||
      item?.cover_image ||
      item?.image_url ||
      item?.image ||
      item?.images?.[0] ||
      ""
  );
  const nightlyPrice =
    toPositiveNumber(item?.display_sell_price) ||
    toPositiveNumber(item?.final_per_night_display_price) ||
    toPositiveNumber(item?.sell_price_per_night) ||
    toPositiveNumber(item?.sellPricePerNight) ||
    toPositiveNumber(item?.price_from) ||
    toPositiveNumber(item?.price_per_night) ||
    toPositiveNumber(item?.base_price_per_night) ||
    toPositiveNumber(item?.price);
  return {
    id: String(item?.id || `stay-${providerPropertyId}`),
    title,
    location,
    price: nightlyPrice,
    rating: toPositiveNumber(item?.avg_rating) || toPositiveNumber(item?.rating) || item?.rating || "",
    badge: badgeOverride || item?.badge || "",
    image: rawImage,
    link:
      item?.detail_path ||
      item?.link ||
      (providerLink
        ? providerLink
        : item?.slug
        ? `/listings/${item.slug}`
        : "/listings"),
    guests: toPositiveNumber(item?.max_guests) || toPositiveNumber(item?.guests) || undefined,
    bedrooms: toPositiveNumber(item?.bedrooms) || undefined,
    bathrooms: toPositiveNumber(item?.bathrooms) || undefined,
    inventory: toPositiveNumber(item?.inventory) || undefined,
    propertyType: item?.property_type || item?.propertyType || item?.type || "",
    amenities: Array.isArray(item?.amenities) ? item.amenities.slice(0, 3) : [],
  };
};

const railItemNeedsPriceRefresh = (item = {}) => {
  if (!item?.is_external && item?.source !== "api") return false;
  const price = Number(
    item.price ||
      item.display_sell_price ||
      item.final_per_night_display_price ||
      item.sell_price_per_night ||
      item.price_per_night ||
      item.base_price_per_night ||
      0
  );
  return !price || price === 108000 || price === 120000 || price === 240000;
};

const refreshProviderRailPrices = async (items = []) => {
  const dateRange = buildDefaultProviderDateRange();
  const refreshed = await Promise.all(
    safeArray(items).map(async (item) => {
      if (!railItemNeedsPriceRefresh(item)) return item;
      const providerId = extractElivaasProviderId(item) || item.provider_property_id || item.providerPropertyId;
      if (!providerId) return item;
      try {
        const detail = await apiGet(
          `/api/providers/elivaas/properties/${encodeURIComponent(providerId)}?${new URLSearchParams({
            checkIn: dateRange.checkIn,
            checkOut: dateRange.checkOut,
            adults: "2",
            children: "0",
          }).toString()}`
        );
        const source = detail?.data || detail?.item || detail || {};
        const refreshedPrice =
          toPositiveNumber(source.price_per_night) ||
          toPositiveNumber(source.pricePerNight) ||
          toPositiveNumber(source.sell_price_per_night) ||
          toPositiveNumber(source.display_sell_price) ||
          toPositiveNumber(source.base_price_per_night) ||
          toPositiveNumber(source.provider_base_price);
        if (!refreshedPrice) return item;
        return {
          ...item,
          ...source,
          id: item.id,
          source: item.source || "api",
          is_external: item.is_external ?? true,
          provider_property_id: providerId,
          price: refreshedPrice,
          price_per_night: refreshedPrice,
          pricePerNight: refreshedPrice,
          sell_price_per_night: refreshedPrice,
          display_sell_price: refreshedPrice,
        };
      } catch (_) {
        return item;
      }
    })
  );
  return refreshed;
};

const railItemIdentity = (item = {}) =>
  String(
    item.provider_property_id ||
      item.providerPropertyId ||
      item.id ||
      item.slug ||
      item.title ||
      item.name ||
      ""
  );

const mergeRailItems = (...groups) => {
  const seen = new Set();
  return groups.flatMap((group) => safeArray(group)).filter((item) => {
    const key = railItemIdentity(item);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const fetchProviderRailBridgeRows = async (sectionKey) => {
  const readRows = async (url) => {
    const response = await fetch(url, { credentials: "include" });
    if (!response.ok) return null;
    const payload = await response.json();
    return safeArray(payload?.items);
  };
  try {
    const rows = await readRows(
      `/homepage-provider-rail.php?${new URLSearchParams({ section: sectionKey, limit: "6" }).toString()}`
    );
    if (rows) return rows;
  } catch (_) {
    return null;
  }
  return null;
};

export default function HomeLocked() {
  const [view, setView] = useState(EMPTY_VIEW);
  const [dynamicRailCards, setDynamicRailCards] = useState(null);
  const [remoteRailDecor, setRemoteRailDecor] = useState(null);
  const [remoteDestinations, setRemoteDestinations] = useState(null);

  useEffect(() => {
    let active = true;

    const loadDestinations = async () => {
      try {
        const res = await apiGet("/api/public/content/banners?placement=destinations&limit=24");
        const list = Array.isArray(res) ? res : res?.data || res?.items || [];
        const normalized = Array.isArray(list)
          ? list
              .map((item, index) => ({
                id: item.id || item.slug || `dest-${index + 1}`,
                title: item.title || item.name || "Destination",
                subtitle: item.subtitle || item.tagline || "",
                image: item.image || item.image_url || item.imageUrl || "",
                link: item.cta_link || item.ctaLink || item.link || "/listings",
                sort_order: Number(item.sort_order ?? index),
              }))
              .sort((a, b) => Number(a.sort_order) - Number(b.sort_order))
          : [];
        if (active) setRemoteDestinations(normalized);
      } catch (_) {
        if (active) setRemoteDestinations([]);
      }
    };

    const cancelIdle = runWhenIdle(loadDestinations, 1200);

    return () => {
      active = false;
      cancelIdle();
    };
  }, []);

  useEffect(() => {
    let active = true;

    const loadRemoteRailDecor = async () => {
      try {
        const res = await apiGet("/api/public/banners/home");
        const payload =
          res?.data && typeof res.data === "object" && !Array.isArray(res.data)
            ? res.data
            : res;
        const sectionPayload =
          payload?.sections && typeof payload.sections === "object"
            ? payload.sections
            : payload;
        if (!active || !sectionPayload || typeof sectionPayload !== "object") return;

        const nextDecor = Object.fromEntries(
          Object.entries(sectionPayload).map(([sectionKey, sectionValue]) => {
            const sectionHeader = sectionValue?.sectionHeader || sectionValue?.header || null;
            return [
              sectionKey,
              {
                eyebrow: sectionHeader?.eyebrow || "",
                title: sectionHeader?.title || "",
                subtitle: sectionHeader?.subtitle || "",
                topBanners: safeArray(sectionValue?.top || sectionValue?.topBanners).map((banner, index) =>
                  normalizeTopBanner(banner, `${sectionKey}-top-${index + 1}`)
                ),
                sideBanner: normalizeSideBanner(
                  sectionValue?.side || sectionValue?.sideBanner,
                  `${sectionKey}-side`
                ),
              },
            ];
          })
        );

        setRemoteRailDecor(nextDecor);
      } catch (_) {
        if (active) {
          setRemoteRailDecor({});
        }
      }
    };

    const cancelIdle = runWhenIdle(loadRemoteRailDecor, 900);

    return () => {
      active = false;
      cancelIdle();
    };
  }, []);

  useEffect(() => {
    let active = true;

    const loadHighlightedRails = async () => {
      try {
        const payload = await apiGet("/api/public/homepage");
        if (active) {
          setView(buildLockedView(payload || {}));
        }
        const rails = safeArray(payload?.rails || payload?.data?.rails);
        if (active) {
          setDynamicRailCards({});
        }

        Promise.all(
          rails.map(async (section) => {
            const sectionKey = section.key || section.sectionKey || section.id;
            const bridgeRows = await fetchProviderRailBridgeRows(sectionKey);
            const homepageRows = safeArray(section.items);
            const sourceRows =
              Array.isArray(bridgeRows) && bridgeRows.length
                ? bridgeRows
                : homepageRows;
            const rows = await refreshProviderRailPrices(mergeRailItems(sourceRows).slice(0, 6));
            return [
              sectionKey,
              rows.map((item, index) => mapPublicStayCard(item, index, item.badge)),
            ];
          })
        )
          .then((entries) => {
            if (!active) return;
            setDynamicRailCards(
              Object.fromEntries(entries.filter(([, cards]) => Array.isArray(cards) && cards.length))
            );
          })
          .catch(() => {});
      } catch (_) {
        if (active) {
          setView(EMPTY_VIEW);
          setDynamicRailCards({});
        }
      }
    };

    const cancelIdle = runWhenIdle(loadHighlightedRails, 300);

    return () => {
      active = false;
      cancelIdle();
    };
  }, []);

  const mergedRails = useMemo(
    () =>
      view.rails.map((section) => {
        const sectionKey = section.sectionKey || section.id;
        const dynamicCards = dynamicRailCards?.[sectionKey];
        const remoteDecor = remoteRailDecor?.[sectionKey];
        const nextSection = {
          ...section,
          eyebrow: remoteDecor?.eyebrow || section.eyebrow,
          title: remoteDecor?.title || section.title,
          subtitle: remoteDecor?.subtitle || section.subtitle,
          topBanners:
            Array.isArray(remoteDecor?.topBanners) && remoteDecor.topBanners.length
              ? remoteDecor.topBanners
              : [],
          sideBanner: hasMeaningfulSideBannerContent(remoteDecor?.sideBanner)
            ? remoteDecor.sideBanner
            : section.sideBanner || null,
          cards: Array.isArray(dynamicCards) && dynamicCards.length ? dynamicCards : [],
        };
        return nextSection;
      }),
    [view.rails, dynamicRailCards, remoteRailDecor]
  );
  const visibleRails = mergedRails.filter(
    (section) =>
      safeArray(section.topBanners).length ||
      section.sideBanner ||
      safeArray(section.cards).length
  );

  return (
    <div className="bg-[var(--page-bg)] text-[var(--ink)] font-jakarta">
      <HeroBanner
        fallbackContent={{}}
        fixedBanners={[]}
        preferRemote
      />
      {visibleRails.map((section, index) => (
        <div
          key={section.sectionKey || section.id}
          className={index % 2 === 0 ? "bg-[var(--page-bg)]" : "bg-[var(--surface)]"}
        >
          <StayRow
            id={section.id}
            eyebrow={section.eyebrow}
            title={section.title}
            subtitle={section.subtitle}
            actionLabel={section.actionLabel}
            actionLink={section.actionLink}
            topBanners={section.topBanners}
            sideBanner={section.sideBanner}
            cards={section.cards}
            variant={(section.sectionKey || section.id) === "curated" ? "gateway" : "rail"}
          />
        </div>
      ))}

      <div id="destinations">
        {safeArray(remoteDestinations).length ? <PopularDestinations items={remoteDestinations} /> : null}
      </div>

      {safeArray(view.confidenceItems).length ? (
        <WhyHomevilasVideoCarousel
          eyebrow={view.confidenceHeader.eyebrow}
          title={view.confidenceHeader.title}
          subtitle={view.confidenceHeader.subtitle}
          ambientImage={view.confidenceBackgroundImage}
          videos={[]}
          fallbackItems={view.confidenceItems}
        />
      ) : null}

      {safeArray(view.blogs).length ? <JournalGrid items={view.blogs} /> : null}
    </div>
  );
}
